import {
  AbsoluteCenter,
  Fade,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { Suspense, memo, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from 'urql';
import { useTranslation } from 'react-i18next';

import PropertiesPanel from '../components/common/PropertiesPanel';
import ViewerInteractions from '../components/common/forgeViewerInteractions/ViewerInteractions';
import { Sidebar } from '../components/layouts/layouts';
import ShapesPanel from '../components/orderContractor/ShapesPanel';
import ModelsPanel from '../components/project/ModelsPanel';
import { OrdersPanel } from '../components/project/orderspanel/OrdersPanel';
import { GetProjectDeepDocument } from '../gql/graphql';
import { useDocumentTitle } from '../hooks/document';
import { useResolvedDbIds } from '../hooks/order';
import { UserType, useUserTenant } from '../services/auth-info';
import { StatusPanel } from 'src/components/orderContractor/StatusPanel';
import { CopilotToolbar } from 'src/components/orderContractor/CopilotToolbar';
import { Point } from 'src/domain/geometry/geometric-types';
import { useDrawer } from 'src/components/common/DrawerContext';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';

interface OrderContractorProps {
  projectId: string;
}

export function OrderContractor({
  projectId,
}: OrderContractorProps): React.ReactElement | null {
  const user = useUserTenant();
  const isContractor = user.userDetails.userType === UserType.Contractor;
  const { closeDrawer: closeEventHistoryDrawer, isDrawerOpen } = useDrawer();
  const readOnly = !isContractor;

  const { viewerMode } = useViewerMode();

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId },
  });

  const project = projectData?.project;

  const loadingTextColor = useColorModeValue('gray.500', 'gray.300');

  useDocumentTitle(project?.name);

  useEffect(() => {
    return () => {
      if (isDrawerOpen) closeEventHistoryDrawer();
    };
  }, [isDrawerOpen]);

  const { t } = useTranslation('project');
  const resolvingOrdersSpinnerTitle = t('resolving-table-items');

  const cleanedProjectSettings = useMemo(() => {
    return {
      maxTrianglesForComplexAlgorithm:
        project?.settings?.polygonCalculationLimit || 5000,
      globalOffset: [
        project?.settings?.viewerGlobalOffset?.x || 0,
        project?.settings?.viewerGlobalOffset?.y || 0,
        project?.settings?.viewerGlobalOffset?.z || 0,
      ] as Point,
    };
  }, [project?.settings]);

  const resolvedDbids = useResolvedDbIds(
    projectId,
    project?.orders ?? [],
    cleanedProjectSettings
  );

  if (!project) return null;

  return (
    <React.Fragment>
      <Sidebar positioning="left" zIndex={99}>
        {!(user.isProtan && viewerMode === ViewerMode.SheetsV2) && (
          <StatusPanel
            project={project}
            isContractor={isContractor}
            isProtan={user.isProtan}
            resolvedDbids={resolvedDbids}
          />
        )}
        <OrdersPanel
          project={project}
          orders={project.orders}
          resolvedDbIds={resolvedDbids}
          isContractor={isContractor}
          readOnly={readOnly}
        />
      </Sidebar>
      <CopilotToolbar
        isContractor={isContractor}
        projectId={projectId}
        projectSettings={cleanedProjectSettings}
      />
      <Suspense
        fallback={
          <Spin
            title={resolvingOrdersSpinnerTitle}
            color={loadingTextColor}
            loading
          />
        }
      >
        <Outlet />
      </Suspense>
      <Sidebar positioning="right" zIndex={99}>
        <ModelsPanel
          projectId={projectId}
          translationType={project.settings?.translationType}
          readOnly={readOnly}
        />
        {readOnly || <ShapesPanel readOnly={readOnly} projectId={project.id} />}
        <PropertiesPanel
          settings={project.settings || undefined}
          projectId={projectId}
        />
      </Sidebar>
      <ViewerInteractions
        projectId={projectId}
        resolvedDbIds={resolvedDbids}
        readOnly={readOnly}
      />
    </React.Fragment>
  );
}

export default memo(OrderContractor);

const Spin = ({
  loading,
  color,
  title,
}: {
  loading: boolean;
  color: string;
  title: string;
}) => {
  return (
    <Fade in={loading} unmountOnExit delay={1}>
      <AbsoluteCenter axis="horizontal" textAlign={'center'} bottom={10}>
        <Spinner color="brand.400" />
        <Text size="sm" color={color}>
          {title}
        </Text>
      </AbsoluteCenter>
    </Fade>
  );
};
